import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import stadiumBg from "../../assets/images/streamer-stadium-bg.jpg";
import mainLogo from "../../assets/images/main_logo.svg";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MdKeyboardArrowDown, MdKeyboardArrowUp, MdSearch, MdContentCopy, MdClear, MdRefresh } from "react-icons/md";
import toast from "react-hot-toast";
import { Api } from "../../services/interceptor";
import useDebounce from "../../hooks/useDebounce";
import "./StreamerDashboard.scss";
import { useAppSelector } from "../../redux/hooks";
import { NetworkQualityIndicator } from "../../components/common/NetworkQualityIndicator";
import { isTestMatch } from "../../helper/helper";
import FieldPlacementDialog from "./FieldPlacementDialog";
import PersonIntroDialog from "./PersonIntroDialog";
import type { FieldPoint } from "./fieldPlacement";
import { useTournamentMatches } from "../tournamentPage/useTournamentMatches";

interface LeagueOption {
  leagueId: string;
  leagueName: string;
}

interface TournamentOption {
  tournamentId: string;
  tournamentName: string;
}

interface MatchOption {
  matchId: string;
  teamOne?: { teamName?: string; teamSnapshotId?: string };
  teamTwo?: { teamName?: string; teamSnapshotId?: string };
  matchStatus?: string;
  startDate?: string;
  groundId?: { groundName?: string };
  ground?: { groundName?: string };
}

interface OverlayItem {
  display: string;
  internal: string;
  special?: "wicket" | "playerOut" | "message" | "nextMatch" | "battingScorecard" | "bowlingScorecard" | "playerStats" | "matchPlayerStats" | "pointsTable" | "inningChart" | "playerWagonWheel" | "strategicTimeout" | "inningsBreak" | "reviewTaken" | "squadDetails" | "showTarget" | "drsTimer" | "winPrediction" | "fieldPlacement" | "personIntro";
  // Only shown when the Custom Colors theme is selected - the overlay this fires has no
  // rendering in any other theme.
  customColorsOnly?: boolean;
}

interface DivisionResponse {
  divisionId?: string;
  divisionName?: string;
  childDivisions?: DivisionResponse[];
  teams?: Array<{ teamSnapshotId?: string; teamName?: string }>;
}

interface TournamentDivisionsResponse {
  tournamentId?: string;
  divisionResponseList?: DivisionResponse[];
}

interface BattingScoreCardItem {
  playerId?: string;
  playerFirstName?: string;
  playerLastName?: string;
  run?: number;
  balls?: number;
  wicket?: { type?: string };
  outString?: string;
}

interface InningScorecard {
  inningId?: string;
  inningType?: string;
  inningStatus?: string;
  over?: number;
  ball?: number;
  battingTeamId?: string;
  bowlingTeamId?: string;
  battingTeamName?: string;
  bowlingTeamName?: string;
  battingScoreCardResponseList?: BattingScoreCardItem[];
}

interface MatchDetails {
  teamOne?: MatchOption["teamOne"];
  teamTwo?: MatchOption["teamTwo"];
  divisionId?: string;
  matchType?: string;
  inningScorecardList?: InningScorecard[];
  tournament?: { tournamentId?: string };
}

// A row of the live scorecard, kept as the raw object /getScoreByMatch returns (every field the
// backend's BattingScoreCardResponse/BowlingScoreCardResponse carries, not just the ones the edit
// table shows) so an unedited field still round-trips correctly in the override payload.
type ScoreCardRow = Record<string, unknown>;

interface ScoreCardEditRow {
  included: boolean;
  row: ScoreCardRow;
}

interface FullInningScorecard {
  inningId?: string;
  battingScoreCardResponseList?: ScoreCardRow[];
  bowlingScoreCardResponses?: ScoreCardRow[];
}

interface FullScoreCardResponse {
  inningScoreCardList?: FullInningScorecard[];
}

interface TeamPlayer {
  playerId?: string;
  playerFirstName?: string;
  playerLastName?: string;
}

interface TeamPlayersResponse {
  playingSquad?: TeamPlayer[];
}

interface TickerTheme {
  id: number;
  name: string;
  slug: string;
  active: boolean;
  teamOneColor?: string | null;
  teamTwoColor?: string | null;
  baseColor?: string | null;
}

// A GraphicsType.CustomTemplate row (see TickerTemplates.tsx) - a ticker template with no fixed
// GraphicsType of its own, fired by its own id rather than one of the built-in chips below.
interface CustomTemplateSummary {
  id: number;
  name: string;
  active: boolean;
}

const MIN_OVERS_FOR_WIN_PREDICTION = 6;

// These themes show one team's squad at a time, so "Squad Details" asks which team; the others show both teams.
const SINGLE_SQUAD_THEME_SLUGS = ["bca-wpl", "custom-colors"];

// Curated teamOneColor/teamTwoColor/baseColor combinations for the "Custom Colors" theme, shown as
// one-click presets so a streamer isn't stuck hand-picking three hex values. "Classic" approximates
// the theme's own hardcoded defaults (ticker-overlay's THEME_CONFIG[CUSTOM_COLORS] and the various
// near-black literals the components fall back to), so selecting it looks close to not picking
// colors at all. The rest were chosen and WCAG-contrast-checked (both team colors >=4.5:1 against
// white ticker text, >=100 degrees of hue separation between the two) rather than picked by eye.
interface ColorPreset {
  name: string;
  teamOneColor: string;
  teamTwoColor: string;
  baseColor: string;
}

const CUSTOM_COLOR_PRESETS: ColorPreset[] = [
  { name: "Classic (Purple & Gold)", teamOneColor: "#582F83", teamTwoColor: "#C9AC10", baseColor: "#16111B" },
  { name: "Royal Blue & Fiery Red", teamOneColor: "#1565C0", teamTwoColor: "#C62828", baseColor: "#0D1B2A" },
  { name: "Teal & Crimson", teamOneColor: "#00838F", teamTwoColor: "#B71C1C", baseColor: "#0B1E2D" },
  { name: "Maroon & Steel Blue", teamOneColor: "#8E1B2B", teamTwoColor: "#2E5090", baseColor: "#1A1512" },
  { name: "Violet & Dark Teal", teamOneColor: "#6A1B9A", teamTwoColor: "#00695C", baseColor: "#1C1024" },
  { name: "Navy & Amber-Red", teamOneColor: "#1A3A6B", teamTwoColor: "#C2410C", baseColor: "#0E1526" },
  { name: "Deep Rose & Slate Blue", teamOneColor: "#AD1457", teamTwoColor: "#303F9F", baseColor: "#200B14" },
];

const overlayGroups: Array<{ label: string; items: OverlayItem[] }> = [
  {
    label: "Live Preview (Bottom Ticker Overlays)",
    items: [
      { display: "Four", internal: "Four" },
      { display: "Six", internal: "Six" },
      { display: "Wicket", internal: "Wicket", special: "wicket" },
      { display: "Player Out", internal: "PlayerOut", special: "playerOut" },
      { display: "New Ball", internal: "NewBall" },
    ],
  },
  {
    label: "Match Events",
    items: [
      { display: "Tea Break", internal: "TeaBreak" },
      { display: "Lunch Break", internal: "LunchBreak" },
      { display: "Bad Light", internal: "BadLights" },
      { display: "Weather Info", internal: "WeatherInfo" },
      { display: "Show Match Message", internal: "HeaderMessage", special: "message" },
      { display: "Bottom Ticker Message", internal: "MatchMessage", special: "message", customColorsOnly: true },
      { display: "Strategic Timeout", internal: "StrategicTimeout", special: "strategicTimeout" },
      { display: "Innings Break", internal: "InningBreak", special: "inningsBreak" },
      { display: "Review Taken", internal: "ReviewTaken", special: "reviewTaken" },
      { display: "Decision Pending", internal: "DecisionPending" },
      { display: "DRS Timer", internal: "DrsTimer", special: "drsTimer" },
      { display: "Field Placement", internal: "FieldPlacement", special: "fieldPlacement" },
    ],
  },
  {
    label: "Information",
    items: [
      { display: "Match Summary", internal: "DaySummary" },
      // { display: 'Inning End Summary', internal: 'InningEndSummary' },
      { display: "Toss Win Ratio", internal: "TossResult" },
      { display: "Preview Match", internal: "NextMatch", special: "nextMatch" },
      { display: "Win Prediction", internal: "WinPrediction", special: "winPrediction" },
      { display: "Current Partnership", internal: "CurrentPartnership" },
      { display: "Target", internal: "Target", special: "showTarget" },
      { display: "Squad Details", internal: "PlayingSquad", special: "squadDetails" },
      { display: "Person Intro", internal: "PersonIntro", special: "personIntro" },
    ],
  },
  {
    label: "Scorecards",
    items: [
      { display: "Batting Scorecard", internal: "BattingScoreCard", special: "battingScorecard" },
      { display: "Bowling Scorecard", internal: "BowlingScoreCard", special: "bowlingScorecard" },
      { display: "Player Stats", internal: "BatterStats", special: "playerStats" },
      { display: "Match Player Stats", internal: "MatchPlayerStats", special: "matchPlayerStats" },
    ],
  },
  {
    label: "Charts",
    items: [
      { display: "Worm Chart", internal: "InningsChart" },
      { display: "Manhattan Chart", internal: "RunsChart" },
      { display: "Team Partnership Chart", internal: "PartnershipChart", special: "inningChart" },
      { display: "Run Rate Chart", internal: "RunRateChart" },
      { display: "Batter Chart", internal: "BatterVsBatterChart", special: "inningChart" },
      { display: "Bowler Chart", internal: "BowlerVsBowlerChart", special: "inningChart" },
      { display: "Extras Chart", internal: "ExtrasChart", special: "inningChart" },
      { display: "Team Wagon Wheel", internal: "WagonWheel", special: "inningChart" },
      { display: "Player Wagon Wheel", internal: "PlayerWagonWheel", special: "playerWagonWheel" },
      // { display: 'Boundary Ball %', internal: 'BoundaryBallPercentageChart' },
    ],
  },
  {
    label: "Top Stats",
    items: [
      { display: "Top 10 Batsman", internal: "Top10Batsman" },
      { display: "Top 10 Bowlers", internal: "Top10Bowlers" },
    ],
  },
  {
    label: "Tournament",
    items: [{ display: "Points Table", internal: "PointsTable", special: "pointsTable" }],
  },
];

// The league / tournament / match picks survive a page refresh and a round trip to Manage Assets or
// Manage Templates (both go back with navigate(-1), which remounts this page with empty state).
// sessionStorage, not localStorage: each browser tab keeps its own picks, so a streamer running two
// matches in two tabs doesn't have one tab's selection overwrite the other's.
const SELECTION_KEY = "streamerDashboardSelection";
type SavedSelection = { league: LeagueOption | null; tournament: TournamentOption | null; match: MatchOption | null };

const readSavedSelection = (): SavedSelection => {
  const empty: SavedSelection = { league: null, tournament: null, match: null };
  try {
    const parsed = JSON.parse(sessionStorage.getItem(SELECTION_KEY) ?? "null");
    if (!parsed?.league?.leagueId) return empty;
    const tournament = parsed.tournament?.tournamentId ? parsed.tournament : null;
    return { league: parsed.league, tournament, match: tournament && parsed.match?.matchId ? parsed.match : null };
  } catch {
    return empty;
  }
};

const StreamerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [saved] = useState(readSavedSelection);
  const userProfile = useAppSelector((s) => s.selectedProfile.playerProfile);

  const [leagueSearch, setLeagueSearch] = useState("");
  const [leagueDropdownOpen, setLeagueDropdownOpen] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState<LeagueOption | null>(saved.league);

  const [tournamentDropdownOpen, setTournamentDropdownOpen] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<TournamentOption | null>(saved.tournament);

  const [matchDropdownOpen, setMatchDropdownOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<MatchOption | null>(saved.match);

  const [previewMode, setPreviewMode] = useState<"preview" | "live">("live");
  // A restored match needs a preview id too (handleMatchSelect makes one on a fresh pick).
  const [previewId, setPreviewId] = useState<string>(() => (saved.match ? crypto.randomUUID() : ""));

  useEffect(() => {
    try {
      if (selectedLeague) {
        sessionStorage.setItem(SELECTION_KEY, JSON.stringify({ league: selectedLeague, tournament: selectedTournament, match: selectedMatch }));
      } else {
        sessionStorage.removeItem(SELECTION_KEY);
      }
    } catch {
      // storage blocked (private mode etc.): the picks just won't survive a refresh
    }
  }, [selectedLeague, selectedTournament, selectedMatch]);

  // const [directId, setDirectId] = useState('');
  // const [youtubeLink, setYoutubeLink] = useState('');

  const [themeDropdownOpen, setThemeDropdownOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<TickerTheme | null>(null);
  const [initialTheme, setInitialTheme] = useState<TickerTheme | null>(null);
  const [themeChangeConfirmOpen, setThemeChangeConfirmOpen] = useState(false);
  const themeRef = useRef<HTMLDivElement>(null);

  // Custom Colors theme: teamOneColor/teamTwoColor/baseColor, defaulting to the "Classic" preset
  // (the theme's own hardcoded colors) until a match's saved colors load, or the streamer picks
  // something else.
  const [teamOneColor, setTeamOneColor] = useState(CUSTOM_COLOR_PRESETS[0].teamOneColor);
  const [teamTwoColor, setTeamTwoColor] = useState(CUSTOM_COLOR_PRESETS[0].teamTwoColor);
  const [baseColor, setBaseColor] = useState(CUSTOM_COLOR_PRESETS[0].baseColor);
  const [applyingColors, setApplyingColors] = useState(false);

  // Dialog state
  const [playerStatsDialogOpen, setPlayerStatsDialogOpen] = useState(false);
  const [playerStatsCurrentMatch, setPlayerStatsCurrentMatch] = useState(false);
  const [playerStatsTab, setPlayerStatsTab] = useState<"batting" | "bowling">("batting");

  const [scorecardDialogOpen, setScorecardDialogOpen] = useState(false);
  const [scorecardType, setScorecardType] = useState<string>("BattingScoreCard");

  // Edit-rows step for BattingScoreCard/BowlingScoreCard only (never charts, which share
  // scorecardDialogOpen/scorecardType above for their own inning picker) — lets the streamer drop a
  // row, fix a name or reorder before the scorecard airs, instead of only ever showing it verbatim.
  const [scoreCardEditOpen, setScoreCardEditOpen] = useState(false);
  const [scoreCardEditType, setScoreCardEditType] = useState<"BattingScoreCard" | "BowlingScoreCard">("BattingScoreCard");
  const [scoreCardEditInningId, setScoreCardEditInningId] = useState<string | undefined>();
  const [scoreCardEditRows, setScoreCardEditRows] = useState<ScoreCardEditRow[]>([]);
  const [scoreCardEditDirty, setScoreCardEditDirty] = useState(false);
  const [scoreCardEditLoading, setScoreCardEditLoading] = useState(false);

  const [playerOutDialogOpen, setPlayerOutDialogOpen] = useState(false);

  const [playerWagonWheelDialogOpen, setPlayerWagonWheelDialogOpen] = useState(false);
  const [playerWagonWheelInningIndex, setPlayerWagonWheelInningIndex] = useState(0);

  const [wicketDialogOpen, setWicketDialogOpen] = useState(false);

  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageText, setMessageText] = useState("");
  // Which chip opened the message dialog - "Show Match Message" (top carousel, every theme) or
  // "Bottom Ticker Message" (Custom Colors' bowler-slot pill). Both share this one dialog.
  const [messageGraphicsType, setMessageGraphicsType] = useState<"HeaderMessage" | "MatchMessage">("HeaderMessage");

  const [pointsTableDialogOpen, setPointsTableDialogOpen] = useState(false);
  const [pointsTableTab, setPointsTableTab] = useState(0);

  const [reviewTakenDialogOpen, setReviewTakenDialogOpen] = useState(false);
  // Field placement: the dialog asks for the fielders' positions first; the last placement sent is kept so the next
  // time it opens it can be adjusted instead of redone.
  const [fieldPlacementDialogOpen, setFieldPlacementDialogOpen] = useState(false);
  const [lastFieldPlacement, setLastFieldPlacement] = useState<FieldPoint[]>([]);
  // Person intro: the dialog asks for a name and a type; the last type is kept for the next one.
  const [personIntroDialogOpen, setPersonIntroDialogOpen] = useState(false);
  const [lastPersonType, setLastPersonType] = useState<string | undefined>(undefined);

  const [squadDialogOpen, setSquadDialogOpen] = useState(false);

  const [breakDurationDialogOpen, setBreakDurationDialogOpen] = useState(false);
  const [breakDurationType, setBreakDurationType] = useState<"StrategicTimeout" | "InningBreak" | "DrsTimer">("StrategicTimeout");
  const [breakDurationMinutes, setBreakDurationMinutes] = useState(0);
  const [breakDurationSeconds, setBreakDurationSeconds] = useState(0);

  const leagueRef = useRef<HTMLDivElement>(null);
  const tournamentRef = useRef<HTMLDivElement>(null);
  const matchRef = useRef<HTMLDivElement>(null);
  const previewWrapperRef = useRef<HTMLDivElement>(null);
  const [iframeScale, setIframeScale] = useState(0.313);

  useEffect(() => {
    const wrapper = previewWrapperRef.current;
    if (!wrapper) return;
    setIframeScale(wrapper.getBoundingClientRect().width / 1920);
    const observer = new ResizeObserver(([entry]) => {
      setIframeScale(entry.contentRect.width / 1920);
    });
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [selectedMatch]);

  const debouncedLeagueSearch = useDebounce(leagueSearch, 400);

  const { data: leagueResults = [], isFetching: leagueLoading } = useQuery<LeagueOption[]>({
    queryKey: ["searchLeagueByName", debouncedLeagueSearch],
    queryFn: async () => {
      if (!debouncedLeagueSearch.trim()) return [];
      const res = await Api.get(`/searchLeagueByName?name=${encodeURIComponent(debouncedLeagueSearch)}`);
      return res.data;
    },
    enabled: !!debouncedLeagueSearch.trim(),
  });

  const { data: tournamentData } = useQuery({
    queryKey: ["getTournamentsByLeagueId", selectedLeague?.leagueId],
    queryFn: async () => {
      const res = await Api.get(`/getTournamentsByLeagueId/${selectedLeague!.leagueId}`);
      return res.data;
    },
    enabled: !!selectedLeague,
  });
  const tournaments: TournamentOption[] = tournamentData?.tournamentResponses ?? [];

  // Same lightweight list the tournament page's Matches tab uses; everything else comes from /match/{id} once a match is picked.
  const tournamentMatches = useTournamentMatches(selectedTournament?.tournamentId);
  const matches: MatchOption[] = [...tournamentMatches.live, ...tournamentMatches.up, ...tournamentMatches.past];

  const { data: matchDetails, refetch: refetchMatchDetails } = useQuery<MatchDetails>({
    queryKey: ["matchDetails", selectedMatch?.matchId],
    queryFn: async () => {
      const res = await Api.get(`/match/${selectedMatch!.matchId}`);
      return res.data;
    },
    enabled: !!selectedMatch,
  });

  const { data: matchTickerData } = useQuery<{ tickerTheme?: TickerTheme }>({
    queryKey: ["getMatchTicker", selectedMatch?.matchId],
    queryFn: async () => {
      const res = await Api.get(`/getMatchTicker/${selectedMatch!.matchId}`);
      return res.data;
    },
    enabled: !!selectedMatch,
  });

  useEffect(() => {
    if (matchTickerData?.tickerTheme) {
      setSelectedTheme(matchTickerData.tickerTheme);
      setInitialTheme(matchTickerData.tickerTheme);
      const { teamOneColor: savedOne, teamTwoColor: savedTwo, baseColor: savedBase } = matchTickerData.tickerTheme;
      setTeamOneColor(savedOne || CUSTOM_COLOR_PRESETS[0].teamOneColor);
      setTeamTwoColor(savedTwo || CUSTOM_COLOR_PRESETS[0].teamTwoColor);
      setBaseColor(savedBase || CUSTOM_COLOR_PRESETS[0].baseColor);
    }
  }, [matchTickerData]);

  const handleGoLive = () => {
    setPreviewMode("live");
    if (selectedTheme && initialTheme && selectedTheme.id !== initialTheme.id) {
      setThemeChangeConfirmOpen(true);
    }
  };

  const handleConfirmThemeChangeForLive = async () => {
    if (selectedMatch && selectedTheme) {
      await Api.put("/match/updateMatchTickerTheme", {
        matchId: selectedMatch.matchId,
        showPreviewMode: false,
        tickerThemeId: selectedTheme.id,
        graphicsType: "ThemeChange",
      });
      setInitialTheme(selectedTheme);
    }
    setThemeChangeConfirmOpen(false);
  };

  const handleCancelThemeChangeForLive = () => {
    setSelectedTheme(initialTheme);
    setThemeChangeConfirmOpen(false);
  };

  // Applies teamOneColor/teamTwoColor/baseColor the same way the theme dropdown applies a theme
  // pick: PUT /match/updateMatchTickerTheme with the current previewMode. In preview mode the
  // colors only show in the preview iframe (not persisted); in live mode they're saved onto the
  // match and a ScoreUpdate event tells the live overlay to pick them up.
  const applyCustomColors = async () => {
    if (!selectedMatch || !selectedTheme) return;
    setApplyingColors(true);
    try {
      const isPreview = previewMode === "preview";
      await Api.put("/match/updateMatchTickerTheme", {
        matchId: selectedMatch.matchId,
        showPreviewMode: isPreview,
        previewId: isPreview ? previewId : undefined,
        tickerThemeId: selectedTheme.id,
        graphicsType: "ThemeChange",
        teamOneColor,
        teamTwoColor,
        baseColor,
      });
      if (!isPreview) {
        setInitialTheme({ ...selectedTheme, teamOneColor, teamTwoColor, baseColor });
      }
    } finally {
      setApplyingColors(false);
    }
  };

  const applyColorPreset = (preset: ColorPreset) => {
    setTeamOneColor(preset.teamOneColor);
    setTeamTwoColor(preset.teamTwoColor);
    setBaseColor(preset.baseColor);
  };

  const latestInning = matchDetails?.inningScorecardList?.at(-1);

  const { data: battingTeamPlayers } = useQuery<TeamPlayersResponse>({
    queryKey: ["teamSquad", selectedMatch?.matchId, latestInning?.battingTeamId],
    queryFn: async () => {
      const res = await Api.get(`/getTeamSquadForMatch/${selectedMatch!.matchId}/${latestInning!.battingTeamId}`);
      return res.data;
    },
    enabled: playerStatsDialogOpen && !!selectedMatch && !!latestInning?.battingTeamId,
  });

  const { data: bowlingTeamPlayers } = useQuery<TeamPlayersResponse>({
    queryKey: ["teamSquad", selectedMatch?.matchId, latestInning?.bowlingTeamId],
    queryFn: async () => {
      const res = await Api.get(`/getTeamSquadForMatch/${selectedMatch!.matchId}/${latestInning!.bowlingTeamId}`);
      return res.data;
    },
    enabled: playerStatsDialogOpen && !!selectedMatch && !!latestInning?.bowlingTeamId,
  });

  const { data: tickerThemes = [] } = useQuery<TickerTheme[]>({
    queryKey: ["getTickerThemes"],
    queryFn: async () => {
      const res = await Api.get("/match/getTickerThemes");
      return res.data;
    },
  });

  const { data: customTemplates = [] } = useQuery<CustomTemplateSummary[]>({
    queryKey: ["ticker-templates", "CustomTemplate"],
    queryFn: async () => {
      const res = await Api.get("/ticker-templates?graphicsType=CustomTemplate");
      return res.data;
    },
  });
  const activeCustomTemplates = customTemplates.filter((t) => t.active);

  const tournamentId = matchDetails?.tournament?.tournamentId;
  const { data: divisionsData } = useQuery<TournamentDivisionsResponse>({
    queryKey: ["getDivisions", tournamentId],
    queryFn: async () => {
      const res = await Api.get(`/getDivisions/${tournamentId}`);
      return res.data;
    },
    enabled: pointsTableDialogOpen && !!tournamentId,
  });
  const childDivisions: DivisionResponse[] = divisionsData?.divisionResponseList?.[0]?.childDivisions ?? [];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (leagueRef.current && !leagueRef.current.contains(e.target as Node)) {
        setLeagueDropdownOpen(false);
      }
      if (tournamentRef.current && !tournamentRef.current.contains(e.target as Node)) {
        setTournamentDropdownOpen(false);
      }
      if (matchRef.current && !matchRef.current.contains(e.target as Node)) {
        setMatchDropdownOpen(false);
      }
      if (themeRef.current && !themeRef.current.contains(e.target as Node)) {
        setThemeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSetupAssets = () => {
    navigate("/streamer-dashboard/asset-setup", {
      state: {
        leagueId: selectedLeague!.leagueId,
        leagueName: selectedLeague!.leagueName,
        tournamentId: selectedTournament!.tournamentId,
        tournamentName: selectedTournament!.tournamentName,
      },
    });
  };

  const handleManageTemplates = () => {
    navigate("/streamer-dashboard/ticker-templates", {
      state: {
        leagueName: selectedLeague?.leagueName,
        tournamentId: selectedTournament?.tournamentId,
        tournamentName: selectedTournament?.tournamentName,
      },
    });
  };

  const handleLeagueSelect = (league: LeagueOption) => {
    setSelectedLeague(league);
    setLeagueSearch("");
    setLeagueDropdownOpen(false);
    setSelectedTournament(null);
    setSelectedMatch(null);
  };

  const handleTournamentSelect = (tournament: TournamentOption) => {
    setSelectedTournament(tournament);
    setTournamentDropdownOpen(false);
    setSelectedMatch(null);
  };

  const handleMatchSelect = (match: MatchOption) => {
    setSelectedMatch(match);
    setPreviewId(crypto.randomUUID());
    setMatchDropdownOpen(false);
  };

  const getMatchLabel = (match: MatchOption) => `${match.teamOne?.teamName ?? "Team A"} vs ${match.teamTwo?.teamName ?? "Team B"}`;

  const [cacheKey, setCacheKey] = useState(() => Date.now());

  useEffect(() => {
    setCacheKey(Date.now());
  }, [selectedMatch, previewMode, previewId]);

  // Re-reads everything the dashboard knows about the match (score / innings for the scorecard and
  // chart pickers, the tournament's match list, the saved ticker theme) and reloads the preview,
  // e.g. after the scorer fixes something or the match list was stale.
  const [refreshingMatch, setRefreshingMatch] = useState(false);
  const refreshMatch = async () => {
    if (!selectedMatch) return;
    setRefreshingMatch(true);
    try {
      tournamentMatches.refetch();
      await Promise.all([
        refetchMatchDetails(),
        queryClient.invalidateQueries({ queryKey: ["getMatchTicker", selectedMatch.matchId] }),
        queryClient.invalidateQueries({ queryKey: ["teamSquad", selectedMatch.matchId] }),
      ]);
      setCacheKey(Date.now());
      toast.success("Match refreshed");
    } catch {
      toast.error("Could not refresh the match. Please try again.");
    } finally {
      setRefreshingMatch(false);
    }
  };

  const iframeSrc = selectedMatch ? (previewMode === "preview" ? `${import.meta.env.VITE_TICKER_BASE_URL}preview/${selectedMatch.matchId}/${previewId}?t=${cacheKey}` : `${import.meta.env.VITE_TICKER_BASE_URL}match/${selectedMatch.matchId}?t=${cacheKey}`) : null;

  // const handleFetchMatch = () => {
  //   if (!directId.trim()) return;
  //   console.log('Fetching by ID:', directId);
  // };

  const postGraphics = async (params: { graphicsType: string; playerId?: string; divisionId?: string; inningId?: string; message?: string; tickerThemeId?: number; durationInSeconds?: number; teamName?: string; teamSnapShotId?: string; teamSnapshotId?: string; wicketType?: string; showTarget?: boolean; battingScoreCardOverride?: ScoreCardRow[]; bowlingScoreCardOverride?: ScoreCardRow[]; templateId?: number; fielderPositions?: FieldPoint[]; personName?: string; personType?: string }) => {
    if (!selectedMatch) return;
    const isPreview = previewMode === "preview";
    await Api.post("/showGraphics", {
      matchId: selectedMatch.matchId,
      showPreviewMode: isPreview,
      previewId: isPreview ? previewId : undefined,
      tickerThemeId: selectedTheme?.id,
      ...params,
    });
  };

  const handleInningRequiredChip = (type: string, freshDetails?: MatchDetails) => {
    const details = freshDetails ?? matchDetails;
    const innings = details?.inningScorecardList ?? [];
    if (innings.length <= 1) {
      postGraphics({ graphicsType: type, inningId: innings[0]?.inningId });
    } else {
      setScorecardType(type);
      setScorecardDialogOpen(true);
    }
  };

  // Fetches the live scorecard for one inning and opens the edit-rows step. Separate from
  // handleInningRequiredChip above (shared with the chart types, which still fire straight off that
  // path, unedited) — scorecards always go through editing, whatever the inning count, since there's
  // no reason to withhold the ability to drop/fix/reorder a row just because there's only one inning.
  const openScoreCardEditor = async (type: "BattingScoreCard" | "BowlingScoreCard", inningId: string) => {
    if (!selectedMatch) return;
    setScoreCardEditType(type);
    setScoreCardEditInningId(inningId);
    setScoreCardEditDirty(false);
    setScoreCardEditOpen(true);
    setScoreCardEditLoading(true);
    try {
      const res = await Api.get<FullScoreCardResponse>(`/getScoreByMatch/${selectedMatch.matchId}`);
      const inning = res.data?.inningScoreCardList?.find((i) => i.inningId === inningId);
      const rows = (type === "BattingScoreCard" ? inning?.battingScoreCardResponseList : inning?.bowlingScoreCardResponses) ?? [];
      setScoreCardEditRows(rows.map((row) => ({ included: true, row })));
    } catch {
      toast.error("Could not load the live scorecard");
      setScoreCardEditOpen(false);
    } finally {
      setScoreCardEditLoading(false);
    }
  };

  const handleScoreCardChip = async (type: "BattingScoreCard" | "BowlingScoreCard", freshDetails?: MatchDetails) => {
    const innings = freshDetails?.inningScorecardList ?? [];
    if (innings.length <= 1) {
      if (innings[0]?.inningId) openScoreCardEditor(type, innings[0].inningId);
      return;
    }
    setScorecardType(type);
    setScorecardDialogOpen(true);
  };

  const resetScoreCardEditorToLive = () => {
    if (scoreCardEditInningId) openScoreCardEditor(scoreCardEditType, scoreCardEditInningId);
  };

  const updateScoreCardEditRow = (index: number, patch: ScoreCardRow) => {
    setScoreCardEditDirty(true);
    setScoreCardEditRows((rows) => rows.map((r, i) => (i === index ? { ...r, row: { ...r.row, ...patch } } : r)));
  };

  const toggleScoreCardEditRow = (index: number) => {
    setScoreCardEditDirty(true);
    setScoreCardEditRows((rows) => rows.map((r, i) => (i === index ? { ...r, included: !r.included } : r)));
  };

  const moveScoreCardEditRow = (index: number, direction: -1 | 1) => {
    setScoreCardEditRows((rows) => {
      const target = index + direction;
      if (target < 0 || target >= rows.length) return rows;
      const next = [...rows];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setScoreCardEditDirty(true);
  };

  const confirmScoreCardEdit = async () => {
    // Unedited (never touched a checkbox/field/reorder): fire exactly as before, no override, so an
    // operator who doesn't use this step sees no behavior change from today.
    if (!scoreCardEditDirty) {
      await postGraphics({ graphicsType: scoreCardEditType, inningId: scoreCardEditInningId });
      setScoreCardEditOpen(false);
      return;
    }
    const orderedRows = scoreCardEditRows.filter((r) => r.included).map((r) => r.row);
    const overrideParams = scoreCardEditType === "BattingScoreCard" ? { battingScoreCardOverride: orderedRows } : { bowlingScoreCardOverride: orderedRows };
    await postGraphics({ graphicsType: scoreCardEditType, inningId: scoreCardEditInningId, ...overrideParams });
    setScoreCardEditOpen(false);
  };

  const handleCustomTemplateClick = (template: CustomTemplateSummary) => {
    if (!selectedMatch) return;
    postGraphics({ graphicsType: "CustomTemplate", templateId: template.id });
  };

  const handleChipClick = async (item: OverlayItem) => {
    if (!selectedMatch) return;
    switch (item.special) {
      case "wicket":
        setWicketDialogOpen(true);
        break;
      case "playerOut": {
        await refetchMatchDetails();
        setPlayerOutDialogOpen(true);
        break;
      }
      case "message":
        setMessageGraphicsType(item.internal === "MatchMessage" ? "MatchMessage" : "HeaderMessage");
        setMessageDialogOpen(true);
        break;
      case "nextMatch":
        postGraphics({ graphicsType: "NextMatch", divisionId: matchDetails?.divisionId });
        break;
      case "battingScorecard": {
        const { data: freshDetails } = await refetchMatchDetails();
        handleScoreCardChip("BattingScoreCard", freshDetails);
        break;
      }
      case "bowlingScorecard": {
        const { data: freshDetails } = await refetchMatchDetails();
        handleScoreCardChip("BowlingScoreCard", freshDetails);
        break;
      }
      case "inningChart": {
        const { data: freshDetails } = await refetchMatchDetails();
        handleInningRequiredChip(item.internal, freshDetails);
        break;
      }
      case "playerWagonWheel": {
        const { data: freshDetails } = await refetchMatchDetails();
        const innings = (freshDetails ?? matchDetails)?.inningScorecardList ?? [];
        setPlayerWagonWheelInningIndex(Math.max(innings.length - 1, 0));
        setPlayerWagonWheelDialogOpen(true);
        break;
      }
      case "playerStats": {
        await refetchMatchDetails();
        setPlayerStatsCurrentMatch(false);
        setPlayerStatsTab("batting");
        setPlayerStatsDialogOpen(true);
        break;
      }
      case "matchPlayerStats": {
        await refetchMatchDetails();
        setPlayerStatsCurrentMatch(true);
        setPlayerStatsTab("batting");
        setPlayerStatsDialogOpen(true);
        break;
      }
      case "pointsTable":
        setPointsTableTab(0);
        setPointsTableDialogOpen(true);
        break;
      case "squadDetails":
        if (selectedTheme && SINGLE_SQUAD_THEME_SLUGS.includes(selectedTheme.slug)) {
          setSquadDialogOpen(true);
        } else {
          postGraphics({ graphicsType: item.internal });
        }
        break;
      case "showTarget":
        postGraphics({ graphicsType: "Target", showTarget: true });
        break;
      case "winPrediction": {
        const { data: freshDetails } = await refetchMatchDetails();
        const details = freshDetails ?? matchDetails;
        if (isTestMatch(details?.matchType)) {
          toast.error("Win Prediction is only supported for T20 format");
          break;
        }
        const innings = details?.inningScorecardList ?? [];
        const latest = innings.at(-1);
        const secondLatest = innings.at(-2);
        // The reply innings of a super over is left as NORMAL by the backend
        // (only the first super-over innings is tagged SUPER_OVER), so also
        // check the innings right before the latest one.
        const isSuperOver = [latest, secondLatest].some((inning) => inning?.inningType?.toUpperCase() === "SUPER_OVER");
        if (latest && isSuperOver) {
          toast.error("Win Prediction is not supported for Super Over");
          break;
        }
        const isInningInProgress = latest?.inningStatus?.toUpperCase() === "STARTED";
        const oversCompleted = latest?.over ?? 0;
        if (isInningInProgress && oversCompleted < MIN_OVERS_FOR_WIN_PREDICTION) {
          toast.error(`Win Prediction is available once the first ${MIN_OVERS_FOR_WIN_PREDICTION} overs of the innings are completed`);
          break;
        }
        postGraphics({ graphicsType: "WinPrediction" });
        break;
      }
      case "drsTimer":
        setBreakDurationType("DrsTimer");
        setBreakDurationMinutes(0);
        setBreakDurationSeconds(0);
        setBreakDurationDialogOpen(true);
        break;
      case "reviewTaken":
        setReviewTakenDialogOpen(true);
        break;
      case "fieldPlacement":
        setFieldPlacementDialogOpen(true);
        break;
      case "personIntro":
        setPersonIntroDialogOpen(true);
        break;
      case "strategicTimeout":
        setBreakDurationType("StrategicTimeout");
        setBreakDurationMinutes(0);
        setBreakDurationSeconds(0);
        setBreakDurationDialogOpen(true);
        break;
      case "inningsBreak":
        setBreakDurationType("InningBreak");
        setBreakDurationMinutes(0);
        setBreakDurationSeconds(0);
        setBreakDurationDialogOpen(true);
        break;
      default:
        postGraphics({ graphicsType: item.internal });
    }
  };

  const outBatsmen = latestInning?.battingScoreCardResponseList?.map((b, idx) => ({ ...b, battingOrder: idx + 1 })).filter((b) => b.wicket != null) ?? [];

  const currentPlayers = playerStatsTab === "batting" ? (battingTeamPlayers?.playingSquad ?? []) : (bowlingTeamPlayers?.playingSquad ?? []);

  const getPlayerStatsType = (tab: "batting" | "bowling") => {
    if (tab === "batting") return playerStatsCurrentMatch ? "CurrentMatchBatterStats" : "BatterStats";
    return playerStatsCurrentMatch ? "CurrentMatchBowlerStats" : "BowlerStats";
  };

  if (userProfile?.streamerProfile == null) {
    return (
      <div className="streamer-dashboard streamer-dashboard--unauthorized">
        <div className="sd-unauthorized">
          <img src={mainLogo} alt="Cric Center" className="sd-unauthorized__logo" />
          <div className="sd-unauthorized__divider" />
          <h1 className="sd-unauthorized__title">Access Restricted</h1>
          <p className="sd-unauthorized__message">You are not authorized to view the Streamer Dashboard.</p>
          <p className="sd-unauthorized__hint">This area is available only to approved streamers. If you believe this is a mistake, please complete your streamer profile or contact the Cric Center team for access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="streamer-dashboard">
      {/* ── Filter Bar ── */}
       {/* add a button with title setup assets */}
      <div className="sd-filter-bar">
        <div className="sd-filter-bar__options">
          {/* League */}
          <div className="sd-dropdown" ref={leagueRef}>
            <button type="button" className="sd-dropdown__trigger" onClick={() => setLeagueDropdownOpen((v) => !v)}>
              <span className={selectedLeague ? "sd-dropdown__value" : "sd-dropdown__placeholder"}>{selectedLeague ? selectedLeague.leagueName : "Select League"}</span>
              {leagueDropdownOpen ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
            </button>

            {leagueDropdownOpen && (
              <div className="sd-dropdown__menu">
                <div className="sd-dropdown__search-row">
                  <MdSearch className="sd-dropdown__search-icon" />
                  <input autoFocus type="text" placeholder="Search league..." value={leagueSearch} onChange={(e) => setLeagueSearch(e.target.value)} className="sd-dropdown__search-input" />
                </div>
                <div className="sd-dropdown__list">
                  {leagueLoading && <p className="sd-dropdown__hint">Searching...</p>}
                  {!leagueLoading && debouncedLeagueSearch && leagueResults.length === 0 && <p className="sd-dropdown__hint">No leagues found</p>}
                  {!debouncedLeagueSearch && !leagueLoading && <p className="sd-dropdown__hint">Type to search leagues</p>}
                  {leagueResults.map((league) => (
                    <button key={league.leagueId} type="button" className="sd-dropdown__item" onClick={() => handleLeagueSelect(league)}>
                      {league.leagueName}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Tournament */}
          <div className="sd-dropdown" ref={tournamentRef}>
            <button type="button" className={`sd-dropdown__trigger${!selectedLeague ? " sd-dropdown__trigger--disabled" : ""}`} onClick={() => selectedLeague && setTournamentDropdownOpen((v) => !v)}>
              <span className={selectedTournament ? "sd-dropdown__value" : "sd-dropdown__placeholder"}>{selectedTournament ? selectedTournament.tournamentName : "Select Tournaments"}</span>
              {tournamentDropdownOpen ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
            </button>

            {tournamentDropdownOpen && (
              <div className="sd-dropdown__menu">
                <div className="sd-dropdown__list">
                  {tournaments.length === 0 ? (
                    <p className="sd-dropdown__hint">No tournaments found</p>
                  ) : (
                    tournaments.map((t) => (
                      <button key={t.tournamentId} type="button" className="sd-dropdown__item" onClick={() => handleTournamentSelect(t)}>
                        {t.tournamentName}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Match */}
          <div className="sd-dropdown" ref={matchRef}>
            <button type="button" className={`sd-dropdown__trigger${!selectedTournament ? " sd-dropdown__trigger--disabled" : ""}`} onClick={() => selectedTournament && setMatchDropdownOpen((v) => !v)}>
              <span className={selectedMatch ? "sd-dropdown__value" : "sd-dropdown__placeholder"}>{selectedMatch ? getMatchLabel(selectedMatch) : "Select Match"}</span>
              {matchDropdownOpen ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
            </button>

            {matchDropdownOpen && (
              <div className="sd-dropdown__menu">
                <div className="sd-dropdown__list">
                  {matches.length === 0 ? (
                    <p className="sd-dropdown__hint">No matches found</p>
                  ) : (
                    matches.map((m) => {
                      const isSelected = m.matchId === selectedMatch?.matchId;
                      const groundName = m.groundId?.groundName ?? m.ground?.groundName;
                      const date = m.startDate ? new Date(m.startDate).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }) : null;
                      const meta = [date, groundName].filter(Boolean).join(" · ");
                      return (
                        <button key={m.matchId} type="button" aria-selected={isSelected} className={`sd-dropdown__item sd-dropdown__item--match${isSelected ? " sd-dropdown__item--selected" : ""}`} onClick={() => handleMatchSelect(m)}>
                          <div className="sd-dropdown__match-info">
                            <span className="sd-dropdown__match-teams">{getMatchLabel(m)}</span>
                            {meta && <span className="sd-dropdown__match-date">{meta}</span>}
                          </div>
                          {m.matchStatus && <span className="sd-dropdown__item-badge">{m.matchStatus}</span>}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
          <button
            type="button"
            className={`sd-btn ${!selectedLeague || !selectedTournament ? "sd-btn--preview" : "sd-btn--active"}`}
            onClick={handleSetupAssets}
            disabled={!selectedLeague || !selectedTournament}
          >
            Setup Assets
          </button>
          {/* <button type="button" className="sd-btn sd-btn--active" onClick={handleManageTemplates}>
            Manage Templates
          </button> */}

          {/* {/* Direct ID input */}
          {/* <div className="sd-direct-input">
            <input
              type="text"
              placeholder="Paste Match Id / Tournament Id"
              value={directId}
              onChange={e => setDirectId(e.target.value)}
              className="sd-direct-input__field"
            />
            <button
              type="button"
              className="sd-direct-input__btn"
              onClick={handleFetchMatch}
            >
              Fetch Match
            </button>
          </div>  */}

          {/* ── Network Quality Indicator ── */}
          <div className="sd-filter-bar__network">
            <NetworkQualityIndicator />
          </div>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="sd-content">
        {/* Left Panel */}
        <div className="sd-panel sd-panel--left">
          {/* Bottom Ticker Theme header */}
          <div className="sd-section-header">
            <span className="sd-section-header__title">Bottom Ticker Theme</span>
            <div className="sd-dropdown sd-dropdown--theme" ref={themeRef}>
              <button type="button" className="sd-theme-selector" onClick={() => setThemeDropdownOpen((v) => !v)}>
                {selectedTheme ? selectedTheme.name : "Select Theme"}
                {themeDropdownOpen ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
              </button>
              {themeDropdownOpen && (
                <div className="sd-dropdown__menu">
                  <div className="sd-dropdown__list">
                    {tickerThemes
                      .filter((t) => t.active)
                      .map((theme) => (
                        <button
                          key={theme.id}
                          type="button"
                          className="sd-dropdown__item"
                          onClick={async () => {
                            setSelectedTheme(theme);
                            setThemeDropdownOpen(false);
                            if (selectedMatch) {
                              const isPreview = previewMode === "preview";
                              await Api.put("/match/updateMatchTickerTheme", {
                                matchId: selectedMatch.matchId,
                                showPreviewMode: isPreview,
                                previewId: isPreview ? previewId : undefined,
                                tickerThemeId: theme.id,
                                graphicsType: "ThemeChange",
                              });
                              if (!isPreview) {
                                setInitialTheme(theme);
                              }
                            }
                          }}
                        >
                          {theme.name}
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Custom Colors theme: team one / team two / base color pickers + recommended combos */}
          {selectedTheme?.slug === "custom-colors" && (
            <div className="sd-custom-colors">
              <span className="sd-custom-colors__label">Recommended combinations</span>
              <div className="sd-custom-colors__presets">
                {CUSTOM_COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset.name}
                    type="button"
                    className="sd-custom-colors__preset"
                    title={preset.name}
                    onClick={() => applyColorPreset(preset)}
                  >
                    <span className="sd-custom-colors__preset-swatches">
                      <span className="sd-custom-colors__swatch" style={{ background: preset.teamOneColor }} />
                      <span className="sd-custom-colors__swatch" style={{ background: preset.teamTwoColor }} />
                      <span className="sd-custom-colors__swatch" style={{ background: preset.baseColor }} />
                    </span>
                    <span className="sd-custom-colors__preset-name">{preset.name}</span>
                  </button>
                ))}
              </div>

              <div className="sd-custom-colors__pickers">
                <label className="sd-custom-colors__picker">
                  <span>Team One</span>
                  <input type="color" value={teamOneColor} onChange={(e) => setTeamOneColor(e.target.value)} />
                </label>
                <label className="sd-custom-colors__picker">
                  <span>Team Two</span>
                  <input type="color" value={teamTwoColor} onChange={(e) => setTeamTwoColor(e.target.value)} />
                </label>
                <label className="sd-custom-colors__picker">
                  <span>Base</span>
                  <input type="color" value={baseColor} onChange={(e) => setBaseColor(e.target.value)} />
                </label>
              </div>

              <button type="button" className="sd-custom-colors__apply" disabled={!selectedMatch || applyingColors} onClick={applyCustomColors}>
                {applyingColors ? "Applying…" : "Apply Colors"}
              </button>
            </div>
          )}

          {/* Ticker preview bar */}
          {/* <div className="sd-ticker-preview">
            <span className="sd-ticker-preview__placeholder">Score ticker preview</span>
          </div>  */}

          {/* Overlays header */}
          <div className="sd-overlays-header">
            <span className="sd-overlays-header__title">Overlays</span>
            <div className="sd-overlays-header__actions">
              <button type="button" className="sd-btn-refresh-match" disabled={!selectedMatch || refreshingMatch} onClick={() => void refreshMatch()}>
                <MdRefresh className={refreshingMatch ? "sd-spin" : undefined} /> {refreshingMatch ? "Refreshing…" : "Refresh Match"}
              </button>
              <button
                type="button"
                className="sd-btn-clear-overlays"
                disabled={!selectedMatch}
                onClick={() => postGraphics({ graphicsType: "ScoreUpdate" })}
              >
                <MdClear /> Clear Overlays
              </button>
            </div>
          </div>

          {/* Overlay groups */}
          <div className="sd-overlays-body">
            {overlayGroups.map((group, gi) => (
              <div key={group.label} className="sd-overlay-group">
                <h4 className="sd-overlay-group__title">{group.label}</h4>
                <div className="sd-overlay-group__chips">
                  {group.items
                    .filter((item) => !item.customColorsOnly || selectedTheme?.slug === "custom-colors")
                    .map((item) => (
                      <button key={item.internal} type="button" className={`sd-chip${!selectedMatch ? " sd-chip--disabled" : ""}`} onClick={() => handleChipClick(item)} disabled={!selectedMatch}>
                        {item.display}
                      </button>
                    ))}
                </div>
                {gi < overlayGroups.length - 1 && <div className="sd-divider" />}
              </div>
            ))}
            {/* {activeCustomTemplates.length > 0 && (
              <div className="sd-overlay-group">
                <h4 className="sd-overlay-group__title">Custom</h4>
                <div className="sd-overlay-group__chips">
                  {activeCustomTemplates.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      className={`sd-chip${!selectedMatch ? " sd-chip--disabled" : ""}`}
                      onClick={() => handleCustomTemplateClick(template)}
                      disabled={!selectedMatch}
                    >
                      {template.name}
                    </button>
                  ))}
                </div>
              </div>
            )} */}
          </div>
        </div>

        {/* Right Panel */}
        <div className="sd-panel sd-panel--right">
          <h4 className="sd-panel__title">Live / Preview</h4>
          <div className="sd-divider" />

          <div className="sd-preview-area" style={selectedMatch ? { backgroundImage: `url(${stadiumBg})` } : undefined}>
            {iframeSrc ? (
              <div className="sd-preview-area__iframe-wrapper" ref={previewWrapperRef}>
                <iframe
                  src={iframeSrc}
                  title="match-preview"
                  style={{
                    width: "1920px",
                    height: "1080px",
                    transform: `scale(${iframeScale})`,
                    transformOrigin: "0 0",
                    border: "none",
                  }}
                />
              </div>
            ) : (
              <span className="sd-preview-area__label">NO MATCH</span>
            )}
          </div>

          <div className="sd-preview-actions">
            <div className="sd-preview-actions__btns">
              <button type="button" className={`sd-btn sd-btn--preview${previewMode === "preview" ? " sd-btn--active" : ""}`} onClick={() => setPreviewMode("preview")}>
                Preview
              </button>
              <button type="button" className={`sd-btn sd-btn--live${previewMode === "live" ? " sd-btn--active" : ""}`} onClick={handleGoLive}>
                Go Live
              </button>
              <button
                type="button"
                className="sd-btn sd-btn--copy"
                disabled={!selectedMatch}
                onClick={() => {
                  if (!selectedMatch) return;
                  const liveUrl = `${import.meta.env.VITE_TICKER_BASE_URL}match/${selectedMatch.matchId}`;
                  navigator.clipboard.writeText(liveUrl);
                }}
              >
                <MdContentCopy /> Copy Live Ticker Link
              </button>
            </div>
            <p className="sd-preview-actions__theme">
              <strong>Theme : </strong>
              <span>{selectedTheme?.name ?? "Default"}</span>
            </p>
          </div>
          {/* 
          <div className="sd-youtube-row">
            <input
              type="text"
              placeholder="Paste youtube link (option)"
              value={youtubeLink}
              onChange={e => setYoutubeLink(e.target.value)}
              className="sd-youtube-row__input"
            />
            <button type="button" className="sd-youtube-row__btn">Add Link</button>
          </div> */}
        </div>
      </div>

      {/* ── Player Stats Dialog ── */}
      {playerStatsDialogOpen && (
        <div className="sd-dialog-overlay" onClick={() => setPlayerStatsDialogOpen(false)}>
          <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="sd-dialog__title">{playerStatsCurrentMatch ? "Match Player Stats" : "Player Stats"}</h3>
            <div className="sd-dialog__tabs">
              <button type="button" className={`sd-dialog__tab${playerStatsTab === "batting" ? " sd-dialog__tab--active" : ""}`} onClick={() => setPlayerStatsTab("batting")}>
                Batting Team
              </button>
              <button type="button" className={`sd-dialog__tab${playerStatsTab === "bowling" ? " sd-dialog__tab--active" : ""}`} onClick={() => setPlayerStatsTab("bowling")}>
                Bowling Team
              </button>
            </div>
            <div className="sd-dialog__list">
              {currentPlayers.length === 0 && <p className="sd-dialog__empty">Loading players...</p>}
              {currentPlayers.map((player, i) => (
                <button
                  key={player.playerId ?? i}
                  type="button"
                  className="sd-dialog__list-item"
                  onClick={async () => {
                    await postGraphics({
                      graphicsType: getPlayerStatsType(playerStatsTab),
                      playerId: player.playerId,
                      inningId: latestInning?.inningId,
                    });
                    setPlayerStatsDialogOpen(false);
                  }}
                >
                  {i + 1}. {player.playerFirstName} {player.playerLastName ?? ""}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Scorecard Inning Dialog ── */}
      {scorecardDialogOpen &&
        (() => {
          const innings = matchDetails?.inningScorecardList ?? [];
          const teamName = (inn: InningScorecard | undefined) => (
            scorecardType === "BowlingScoreCard" ||
            scorecardType == "ExtrasChart" ||
            scorecardType == "BowlerVsBowlerChart" ?
            inn?.bowlingTeamName :
            inn?.battingTeamName
          );
          return (
            <div className="sd-dialog-overlay" onClick={() => setScorecardDialogOpen(false)}>
              <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
                <h3 className="sd-dialog__title">Select Inning</h3>
                <div className="sd-dialog__inning-options">
                  {innings.map((inning, index) => {
                    const isCurrent = index === innings.length - 1;
                    return (
                      <button
                        key={inning?.inningId ?? index}
                        type="button"
                        className={`sd-dialog__inning-card ${isCurrent ? "sd-dialog__inning-card--current" : ""}`}
                        onClick={async () => {
                          setScorecardDialogOpen(false);
                          if ((scorecardType === "BattingScoreCard" || scorecardType === "BowlingScoreCard") && inning?.inningId) {
                            openScoreCardEditor(scorecardType, inning.inningId);
                          } else {
                            await postGraphics({ graphicsType: scorecardType, inningId: inning?.inningId });
                          }
                        }}
                      >
                        <span className="sd-dialog__inning-label">{isCurrent ? "Current Inning" : `Inning ${index + 1}`}</span>
                        <span className="sd-dialog__inning-team">{teamName(inning)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}

      {/* ── Player Wagon Wheel Dialog ── */}
      {playerWagonWheelDialogOpen &&
        (() => {
          const innings = matchDetails?.inningScorecardList ?? [];
          const inning = innings[playerWagonWheelInningIndex];
          const batters = inning?.battingScoreCardResponseList ?? [];
          return (
            <div className="sd-dialog-overlay" onClick={() => setPlayerWagonWheelDialogOpen(false)}>
              <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
                <h3 className="sd-dialog__title">Select Batter</h3>
                {innings.length > 1 && (
                  <div className="sd-dialog__tabs">
                    {innings.map((inn, i) => (
                      <button
                        key={inn?.inningId ?? i}
                        type="button"
                        className={`sd-dialog__tab${playerWagonWheelInningIndex === i ? " sd-dialog__tab--active" : ""}`}
                        onClick={() => setPlayerWagonWheelInningIndex(i)}
                      >
                        Inning {i + 1}
                      </button>
                    ))}
                  </div>
                )}
                {inning?.battingTeamName && <div className="sd-dialog__list-item-sub">{inning.battingTeamName}</div>}
                <div className="sd-dialog__list">
                  {batters.length === 0 && <p className="sd-dialog__empty">No batters yet in this inning</p>}
                  {batters.map((batsman, i) => (
                    <button
                      key={batsman.playerId ?? i}
                      type="button"
                      className="sd-dialog__list-item"
                      onClick={async () => {
                        await postGraphics({
                          graphicsType: "PlayerWagonWheel",
                          playerId: batsman.playerId,
                          inningId: inning?.inningId,
                        });
                        setPlayerWagonWheelDialogOpen(false);
                      }}
                    >
                      <div>
                        {i + 1}. {batsman.playerFirstName} {batsman.playerLastName ?? ""}
                      </div>
                      <div className="sd-dialog__list-item-sub">
                        Runs: {batsman.run ?? 0} · Balls: {batsman.balls ?? 0}
                        {batsman.wicket?.type ? ` · ${batsman.outString}` : " · not out"}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

      {/* ── Scorecard Edit Rows Dialog (Batting/Bowling only - drop a row, fix a name, reorder, ── */}
      {/* ── or edit a stat before it airs; "Reset to Live" discards edits and re-fetches)       ── */}
      {scoreCardEditOpen && (
        <div className="sd-dialog-overlay" onClick={() => setScoreCardEditOpen(false)}>
          <div className="sd-dialog sd-dialog--scorecard-edit" onClick={(e) => e.stopPropagation()}>
            <div className="sd-dialog__header">
              <h3 className="sd-dialog__title">{scoreCardEditType === "BattingScoreCard" ? "Batting Scorecard" : "Bowling Scorecard"}</h3>
              <button type="button" className="sd-dialog__close" onClick={() => setScoreCardEditOpen(false)}>
                ✕
              </button>
            </div>

            {scoreCardEditLoading ? (
              <p className="sd-dialog__empty">Loading live scorecard...</p>
            ) : scoreCardEditRows.length === 0 ? (
              <p className="sd-dialog__empty">No rows for this inning yet.</p>
            ) : (
              <div className="sd-scorecard-edit__list">
                {scoreCardEditRows.map((entry, index) => {
                  const r = entry.row;
                  return (
                    <div key={(r.playerId as string) ?? index} className={`sd-scorecard-edit__row${!entry.included ? " sd-scorecard-edit__row--excluded" : ""}`}>
                      <input type="checkbox" checked={entry.included} onChange={() => toggleScoreCardEditRow(index)} title="Show this row" />
                      <div className="sd-scorecard-edit__reorder">
                        <button type="button" disabled={index === 0} onClick={() => moveScoreCardEditRow(index, -1)} title="Move up">
                          ▲
                        </button>
                        <button type="button" disabled={index === scoreCardEditRows.length - 1} onClick={() => moveScoreCardEditRow(index, 1)} title="Move down">
                          ▼
                        </button>
                      </div>
                      <input
                        className="sd-scorecard-edit__name"
                        value={(r.playerFirstName as string) ?? ""}
                        onChange={(e) => updateScoreCardEditRow(index, { playerFirstName: e.target.value })}
                        placeholder="First name"
                      />
                      <input
                        className="sd-scorecard-edit__name"
                        value={(r.playerLastName as string) ?? ""}
                        onChange={(e) => updateScoreCardEditRow(index, { playerLastName: e.target.value })}
                        placeholder="Last name"
                      />
                      {scoreCardEditType === "BattingScoreCard" ? (
                        <>
                          <input
                            className="sd-scorecard-edit__stat"
                            type="number"
                            value={(r.run as number) ?? 0}
                            onChange={(e) => updateScoreCardEditRow(index, { run: Number(e.target.value) })}
                            title="Runs"
                          />
                          <input
                            className="sd-scorecard-edit__stat"
                            type="number"
                            value={(r.balls as number) ?? 0}
                            onChange={(e) => updateScoreCardEditRow(index, { balls: Number(e.target.value) })}
                            title="Balls"
                          />
                        </>
                      ) : (
                        <>
                          <input
                            className="sd-scorecard-edit__stat"
                            type="number"
                            value={(r.wickets as number) ?? 0}
                            onChange={(e) => updateScoreCardEditRow(index, { wickets: Number(e.target.value) })}
                            title="Wickets"
                          />
                          <input
                            className="sd-scorecard-edit__stat"
                            type="number"
                            value={(r.runs as number) ?? 0}
                            onChange={(e) => updateScoreCardEditRow(index, { runs: Number(e.target.value) })}
                            title="Runs conceded"
                          />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="sd-dialog__actions sd-dialog__actions--spread">
              <button type="button" className="sd-dialog__btn-cancel" onClick={resetScoreCardEditorToLive} disabled={scoreCardEditLoading}>
                Reset to Live
              </button>
              <div className="sd-dialog__actions">
                <button type="button" className="sd-dialog__btn-cancel" onClick={() => setScoreCardEditOpen(false)}>
                  Cancel
                </button>
                <button type="button" className="sd-dialog__btn-confirm" onClick={confirmScoreCardEdit} disabled={scoreCardEditLoading}>
                  Show
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Player Out Dialog ── */}
      {playerOutDialogOpen && (
        <div className="sd-dialog-overlay" onClick={() => setPlayerOutDialogOpen(false)}>
          <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="sd-dialog__title">Select Out Batsman</h3>
            <div className="sd-dialog__list">
              {outBatsmen.length === 0 && <p className="sd-dialog__empty">No batsmen are out in the current inning</p>}
              {outBatsmen.map((batsman, i) => (
                <button
                  key={batsman.playerId ?? i}
                  type="button"
                  className="sd-dialog__list-item"
                  onClick={async () => {
                    await postGraphics({
                      graphicsType: "PlayerOut",
                      playerId: batsman.playerId,
                      inningId: latestInning?.inningId,
                    });
                    setPlayerOutDialogOpen(false);
                  }}
                >
                  <div>
                    {batsman.battingOrder}. {batsman.playerFirstName} {batsman.playerLastName ?? ""}
                  </div>
                  <div className="sd-dialog__list-item-sub">
                    Runs: {batsman.run ?? 0} · Balls: {batsman.balls ?? 0}
                    {batsman.wicket?.type ? ` · ${batsman.outString}` : ""}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Match Message Dialog ── */}
      {messageDialogOpen && (
        <div
          className="sd-dialog-overlay"
          onClick={() => {
            setMessageDialogOpen(false);
            setMessageText("");
          }}
        >
          <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="sd-dialog__title">{messageGraphicsType === "MatchMessage" ? "Bottom Ticker Message" : "Match Status Message"}</h3>
            <textarea className="sd-dialog__textarea" placeholder="Type your message here..." value={messageText} onChange={(e) => setMessageText(e.target.value)} />
            <div className="sd-dialog__actions">
              <button
                type="button"
                className="sd-dialog__btn-cancel"
                onClick={() => {
                  setMessageDialogOpen(false);
                  setMessageText("");
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sd-dialog__btn-confirm"
                onClick={async () => {
                  if (!messageText.trim()) return;
                  await postGraphics({ graphicsType: messageGraphicsType, message: messageText.trim() });
                  setMessageDialogOpen(false);
                  setMessageText("");
                }}
              >
                Display
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Squad Details Team Selection Dialog (bca-wpl and custom-colors: one squad at a time) ── */}
      {squadDialogOpen && (
        <div className="sd-dialog-overlay" onClick={() => setSquadDialogOpen(false)}>
          <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="sd-dialog__header">
              <h3 className="sd-dialog__title">Squad Details — Select Team</h3>
              <button type="button" className="sd-dialog__close" onClick={() => setSquadDialogOpen(false)}>✕</button>
            </div>
            <div className="sd-dialog__team-options">
              {[matchDetails?.teamOne ?? selectedMatch?.teamOne, matchDetails?.teamTwo ?? selectedMatch?.teamTwo].map((team, i) => (
                <button
                  key={team?.teamSnapshotId ?? i}
                  type="button"
                  className="sd-dialog__team-card"
                  onClick={async () => {
                    await postGraphics({
                      graphicsType: "PlayingSquad",
                      teamName: team?.teamName,
                      teamSnapshotId: team?.teamSnapshotId,
                    });
                    setSquadDialogOpen(false);
                  }}
                >
                  {team?.teamName ?? `Team ${i + 1}`}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Person Intro Dialog ── */}
      {personIntroDialogOpen && (
        <PersonIntroDialog
          initialType={lastPersonType}
          onClose={() => setPersonIntroDialogOpen(false)}
          onSubmit={async (personName, personType) => {
            await postGraphics({ graphicsType: "PersonIntro", personName, personType });
            setLastPersonType(personType);
            setPersonIntroDialogOpen(false);
          }}
        />
      )}

      {/* ── Field Placement Dialog ── */}
      {fieldPlacementDialogOpen && (
        <FieldPlacementDialog
          initial={lastFieldPlacement}
          onClose={() => setFieldPlacementDialogOpen(false)}
          onSubmit={async (fielders) => {
            await postGraphics({ graphicsType: "FieldPlacement", fielderPositions: fielders });
            setLastFieldPlacement(fielders);
            setFieldPlacementDialogOpen(false);
          }}
        />
      )}

      {/* ── Review Taken Dialog ── */}
      {reviewTakenDialogOpen && (
        <div className="sd-dialog-overlay" onClick={() => setReviewTakenDialogOpen(false)}>
          <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="sd-dialog__header">
              <h3 className="sd-dialog__title">Review Taken — Select Team</h3>
              <button type="button" className="sd-dialog__close" onClick={() => setReviewTakenDialogOpen(false)}>✕</button>
            </div>
            <div className="sd-dialog__team-options">
              {[matchDetails?.teamOne ?? selectedMatch?.teamOne, matchDetails?.teamTwo ?? selectedMatch?.teamTwo].map((team, i) => (
                <button
                  key={team?.teamSnapshotId ?? i}
                  type="button"
                  className="sd-dialog__team-card"
                  onClick={async () => {
                    await postGraphics({ graphicsType: "ReviewTaken", teamName: team?.teamName, teamSnapshotId: team?.teamSnapshotId });
                    setReviewTakenDialogOpen(false);
                  }}
                >
                  {team?.teamName ?? `Team ${i + 1}`}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Break Duration Dialog (Strategic Timeout / Innings Break) ── */}
      {breakDurationDialogOpen && (
        <div
          className="sd-dialog-overlay"
          onClick={() => setBreakDurationDialogOpen(false)}
        >
          <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="sd-dialog__title">
              {breakDurationType === "StrategicTimeout" ? "Strategic Timeout" : breakDurationType === "DrsTimer" ? "DRS Timer" : "Innings Break"}
            </h3>
            <p className="sd-dialog__label">Enter break duration</p>
            <div className="sd-dialog__duration-row">
              <div className="sd-dialog__duration-field">
                <input
                  type="number"
                  className="sd-dialog__duration-input"
                  min={0}
                  value={breakDurationMinutes}
                  onChange={(e) => setBreakDurationMinutes(Math.max(0, parseInt(e.target.value) || 0))}
                />
                <span className="sd-dialog__duration-unit">min</span>
              </div>
              <div className="sd-dialog__duration-field">
                <input
                  type="number"
                  className="sd-dialog__duration-input"
                  min={0}
                  max={59}
                  value={breakDurationSeconds}
                  onChange={(e) => setBreakDurationSeconds(Math.min(59, Math.max(0, parseInt(e.target.value) || 0)))}
                />
                <span className="sd-dialog__duration-unit">sec</span>
              </div>
            </div>
            <div className="sd-dialog__actions">
              <button
                type="button"
                className="sd-dialog__btn-cancel"
                onClick={() => setBreakDurationDialogOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sd-dialog__btn-confirm"
                onClick={async () => {
                  const totalSeconds = breakDurationMinutes * 60 + breakDurationSeconds;
                  await postGraphics({ graphicsType: breakDurationType, durationInSeconds: totalSeconds });
                  setBreakDurationDialogOpen(false);
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Theme Change Confirmation Dialog (Go Live) ── */}
      {themeChangeConfirmOpen && (
        <div className="sd-dialog-overlay" onClick={handleCancelThemeChangeForLive}>
          <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="sd-dialog__title">Change Theme in Live Mode?</h3>
            <p className="sd-dialog__label">
              You changed the theme to "{selectedTheme?.name}" in preview mode. Do you want to apply this theme to the live ticker as well?
            </p>
            <div className="sd-dialog__actions">
              <button type="button" className="sd-dialog__btn-cancel" onClick={handleCancelThemeChangeForLive}>
                No
              </button>
              <button type="button" className="sd-dialog__btn-confirm" onClick={handleConfirmThemeChangeForLive}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Wicket Type Dialog ── */}
      {wicketDialogOpen && (
        <div className="sd-dialog-overlay" onClick={() => setWicketDialogOpen(false)}>
          <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="sd-dialog__header">
              <h3 className="sd-dialog__title">Select Wicket Type</h3>
              <button type="button" className="sd-dialog__close" onClick={() => setWicketDialogOpen(false)}>✕</button>
            </div>
            <div className="sd-dialog__team-options">
              {["RUN OUT", "CAUGHT", "BOWLED", "LBW", "STUMPED"].map((type) => (
                <button
                  key={type}
                  type="button"
                  className="sd-dialog__team-card"
                  onClick={async () => {
                    await postGraphics({
                      graphicsType: "Wicket",
                      wicketType: type,
                    });
                    setWicketDialogOpen(false);
                  }}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Points Table Dialog ── */}
      {pointsTableDialogOpen && (
        <div className="sd-dialog-overlay" onClick={() => setPointsTableDialogOpen(false)}>
          <div className="sd-dialog sd-dialog--wide" onClick={(e) => e.stopPropagation()}>
            <h3 className="sd-dialog__title">Points Table</h3>
            {childDivisions.length === 0 ? (
              <p className="sd-dialog__empty">{tournamentId ? "Loading groups..." : "No tournament info available"}</p>
            ) : (
              <>
                <div className="sd-dialog__tabs">
                  {childDivisions.map((div, i) => (
                    <button key={div.divisionId ?? i} type="button" className={`sd-dialog__tab${pointsTableTab === i ? " sd-dialog__tab--active" : ""}`} onClick={() => setPointsTableTab(i)}>
                      {div.divisionName ?? `Group ${i + 1}`}
                    </button>
                  ))}
                </div>
                <div className="sd-dialog__list">
                  {(childDivisions[pointsTableTab]?.teams ?? []).map((team, i) => (
                    <div key={team.teamSnapshotId ?? i} className="sd-dialog__pt-row">
                      <span className="sd-dialog__pt-rank">{i + 1}</span>
                      <span className="sd-dialog__pt-team">{team.teamName ?? "-"}</span>
                    </div>
                  ))}
                </div>
                <div className="sd-dialog__actions">
                  <button type="button" className="sd-dialog__btn-cancel" onClick={() => setPointsTableDialogOpen(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="sd-dialog__btn-confirm"
                    onClick={async () => {
                      await postGraphics({
                        graphicsType: "PointsTable",
                        divisionId: childDivisions[pointsTableTab]?.divisionId,
                      });
                      setPointsTableDialogOpen(false);
                    }}
                  >
                    Display
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StreamerDashboard;
